import { Injectable } from "@angular/core";

import { JwtModule, JwtHelperService } from '@auth0/angular-jwt';
import { Subject, Observable } from "rxjs";
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

export interface PessoaStorage {
  dataHoraUltimaAlteracao: Date;
  situacaoCadastro?: string;
  nome: string;
  pessoaJuridica?: {
    cnpj: string;
    nomeFantasia: string,
  };
  pessoaFisica?: {
    cpf: string
  }
}

export interface UsuarioStorage {
  idUsuario?:     string;
  email?:         string;
  nomeExibicao?:  string;
  nomeUsuario:    string;
  dataExpiracao:  Date;
  token:          string;
  cargo?:         string;
  secretaria?:    string;
  departamento?:  string;
  setor?:         string;
  funcoes?:       Array<string>;
  idOrg?:         string;
  idDom?:         string;
  idCgm?:         string;
  codUsu?:        string;
  idPes?:         string;
  alterarCmg?:    boolean;
  chaveSistema:   string;
  idCgo:          string;
  idSec:          string;
  idDep:          string;
  idSet:          string;
  loginNome?:     string;
  pessoa?:        PessoaStorage;
  cpf?:           string;
  telefone?:      string;
}

export interface LoginInfoStorage {
  sucesso:      boolean;
  mensagem:     string;
  landingPage:  string;
  statusHttp:   number;  
  usuario?:     UsuarioStorage;
}

@Injectable()
export class UsuarioInfoService {

  private storageSub = new Subject<boolean>();
  public chaveUsuarioAtual: string  = "usuarioAtual";
  public storage: Storage = sessionStorage;

  constructor (
    private http: HttpClient
  ) {}

  watchStorage(): Observable<any> {
    return this.storageSub.asObservable();
  }

  removerInfoUsuario() {
    this.storage.removeItem(this.chaveUsuarioAtual);
    this.storageSub.next(true);
  }

  guardaInfoUsuario (usuarioInfoString: string) {
    this.storage.setItem(this.chaveUsuarioAtual, usuarioInfoString);
    this.storageSub.next(true);
  }

  getInfoUsuario(): UsuarioStorage | null {
    try {

      let usuarioInfoString: string = this.storage.getItem(this.chaveUsuarioAtual);
      if(usuarioInfoString) {
        let usuario: UsuarioStorage = JSON.parse(this.storage.getItem(this.chaveUsuarioAtual));
        return usuario;
      }
      return null;

    } catch (error) {
      return null;
    }
  } //FIM GET INFORMACOES USUARIO

  isLoggedIn(): boolean {
    return this.storage.getItem(this.chaveUsuarioAtual) ? true : false;
  }

  isDateTokenValid(): boolean {
    const helper = new JwtHelperService();    
    let usuario:    UsuarioStorage  = this.getInfoUsuario();
    //let tokenExpirado               = jwtHelper.isTokenExpired(usuario.token['token']);
    let tokenExpirado = helper.isTokenExpired(usuario.token['token']);

    if (tokenExpirado) {
      return false;
    }
    return true;
  }

  getNomeUsuario(): string {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    if (usuario !== null) {
      return usuario.nomeExibicao;
    }
    return "sem-nome-configurado";
  }

  getChaveSessao(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    if (usuario !== null) {
      return usuario.chaveSistema;
    }
    return null;
  }

  getCargoUsuario(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    
    if (usuario !== null && usuario.cargo != '' && usuario.cargo != null)
      return usuario.cargo;
    
    return null;
  }

  setCargoUsuario(cargo: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.cargo = cargo;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getSecretariaUsuario(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();

    if (usuario !== null && usuario.secretaria != '' && usuario.secretaria != null)
      return usuario.secretaria;

    return null;
  }

  setSecretariaUsuario(secretaria: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.secretaria = secretaria;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }
  
  getDepartamentoUsuario(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();

    if (usuario !== null && usuario.departamento != '' && usuario.departamento != null)
      return usuario.departamento;

    return null;
  }

  setDepartamentoUsuario(departamento: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.departamento = departamento;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getSetorUsuario(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();

    if (usuario !== null && usuario.setor != '' && usuario.setor != null)
      return usuario.setor;

    return null;
  }

  setSetorUsuario(setor: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.setor = setor;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getLoginNome(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    
    if (usuario !== null && usuario.loginNome != '' && usuario.loginNome != null)
      return usuario.loginNome;
    
    return null;
  }

  setLoginNome(loginNome: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.loginNome = loginNome;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getStoredToken(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    if (usuario !== null) {
      return usuario.token['token'];
    }    
    return null;
  }

  getIdOrg(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    if (usuario !== null && usuario.idOrg != null && typeof usuario.idOrg != 'undefined') {
      return usuario.idOrg;
    }
    return null;
  }

  setIdOrg(idOrg: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.idOrg = idOrg;    
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  removeIdOrg() {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    delete usuario.idOrg;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getIdCgm(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    if(usuario !== null) {
      return usuario.idCgm;
    }
    return null;
  }

  setIdCgm(idCgm: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.idCgm = idCgm;    
    this.guardaInfoUsuario(JSON.stringify(usuario));
  } 

  setNome(nomeUsuario: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.nomeExibicao = nomeUsuario;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  setEmail(email: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.email = email;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  setIdDom(idDom: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.idDom = idDom;    
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getIdDom(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    if (usuario !== null) {
      return usuario.idDom;
    }
    return null;
  }

  removeIdDom() {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    delete usuario.idDom;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getIdUsu(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    if(usuario !== null) {
      return usuario.idUsuario;
    }
    return null;
  }

  setIdUsu(idUsu: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();    
    usuario.idUsuario = idUsu;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  removeIdUsu() {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    delete usuario.idUsuario;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getRoles(): Array<any> | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    if (usuario !== null) {
      return usuario.funcoes;
    }
    return null;
  }

  getIdCgo(): string | null {
    if (this.getInfoUsuario() !== null) {
      return this.getInfoUsuario().idCgo;
    }
    return null;
  }

  setIdCgo(idCgo: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.idCgo = idCgo;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  removeIdCgo() {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    delete usuario.idCgo;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getIdSec(): string | null {
    if (this.getInfoUsuario() !== null) {
      return this.getInfoUsuario().idSec;
    }
    return null;
  }

  setIdSec(idSec: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.idSec = idSec;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  removeIdSec() {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    delete usuario.idSec;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getIdDep(): string | null {
    if (this.getInfoUsuario() !== null) {
      return this.getInfoUsuario().idDep;
    }
    return null;
  }

  setIdDep(idDep: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.idDep = idDep;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  removeIdDep () {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    delete usuario.idDep;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

	getIdSet(): string | null {
		if (this.getInfoUsuario() !== null) {
  		return this.getInfoUsuario().idSet;
  	}
  	return null;
   }
   
  setIdSet(idSet: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.idSet = idSet;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  removeIdSet() {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    delete usuario.idSet;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getCodUsu() {
    if (this.getInfoUsuario() !== null) {
  		return this.getInfoUsuario().codUsu;
  	}
  	return null;
  }

  setCodUsu(codUsu: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.codUsu = codUsu;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  removeCodUsu() {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    delete usuario.codUsu;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }


  getIdPes() {
    if (this.getInfoUsuario() !== null) {
  		return this.getInfoUsuario().idPes;
  	}
  	return null;
  }

  setIdPes(idPes: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.idPes = idPes;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }
   
  removeIdPes() {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    delete usuario.idPes;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getPessoa() : PessoaStorage | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();

    if (usuario == null || usuario.pessoa == null || typeof usuario.pessoa == 'undefined')
      return null;
    
    return usuario.pessoa;
  }

  setPessoa(pessoa: PessoaStorage) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.pessoa = pessoa;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getNomePessoa() {
    let usuario: UsuarioStorage = this.getInfoUsuario();

    if (usuario == null || usuario.pessoa == null || typeof usuario.pessoa == 'undefined' || typeof usuario.pessoa.nome == 'undefined' || usuario.pessoa.nome == null || usuario.pessoa.nome == '')
      return null;
    
    return usuario.pessoa.nome;
  }

  setNomePessoa(nome: string) {
    let pessoa: PessoaStorage = this.getPessoa();
    pessoa.nome = nome;

    if(pessoa.pessoaJuridica !== null)
      pessoa.pessoaJuridica.nomeFantasia = nome;

    this.setPessoa(pessoa);
  }

  getCpf(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    
    if (usuario !== null && usuario.cpf != '' && usuario.cpf != null)
      return usuario.cpf;
    
    return null;
  }

  setCpf(cpf: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.cpf = cpf;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getTelefone(): string | null {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    
    if (usuario !== null && usuario.telefone != '' && usuario.telefone != null)
      return usuario.telefone;
    
    return null;
  }

  setTelefone(telefone: string) {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.telefone = telefone;
    this.guardaInfoUsuario(JSON.stringify(usuario));
  }

  getPessoaJuridica () {
    let usuario: UsuarioStorage = this.getInfoUsuario();

    if (usuario == null || usuario.pessoa == null || typeof usuario.pessoa == 'undefined' || typeof usuario.pessoa.pessoaJuridica == 'undefined' || usuario.pessoa.pessoaJuridica == null)
      return null;

    return usuario.pessoa.pessoaJuridica;
  }

  getPessoaFisica () {
    let usuario: UsuarioStorage = this.getInfoUsuario();

    if (usuario == null || usuario.pessoa == null || typeof usuario.pessoa == 'undefined' || typeof usuario.pessoa.pessoaFisica == 'undefined' || usuario.pessoa.pessoaFisica == null)
      return null;

    return usuario.pessoa.pessoaFisica;
  }

  getSituacaoCadastro () {
    let usuario: UsuarioStorage = this.getInfoUsuario();

    if (usuario == null || usuario.pessoa == null || typeof usuario.pessoa == 'undefined' || typeof usuario.pessoa.situacaoCadastro == 'undefined' || usuario.pessoa.situacaoCadastro == null || usuario.pessoa.situacaoCadastro == '')
      return null;

    return usuario.pessoa.situacaoCadastro;
  }

  removerPessoa () {
    let usuario: UsuarioStorage = this.getInfoUsuario();
    usuario.idPes = null;
    delete usuario.pessoa;
    this.guardaInfoUsuario(JSON.stringify(usuario));    
  }

  setPessoaStorage (dadosPessoa: any) {
    let pessoaStorage = {
      dataHoraUltimaAlteracao: null,
      situacaoCadastro: '',
      nome: '',
        pessoaJuridica: {
          cnpj: '',
          nomeFantasia: '',
        },
        pessoaFisica: {
          cpf: ''
        }
    };

    pessoaStorage['dataHoraUltimaAlteracao'] = dadosPessoa.dataHoraUltimaAlteracao;
    pessoaStorage['nome'] = dadosPessoa.nome;
    pessoaStorage['situacaoCadastro'] = dadosPessoa.situacaoCadastro;

    //DADOS RECEBIDOS DO ENTPOINT /servercad/api/dados/pessoa/cpfcnpj/{cpfCnpj}
    if (typeof dadosPessoa['cpfCnpj'] !== 'undefined' && dadosPessoa['cpfCnpj'] !== null && dadosPessoa['cpfCnpj'] != '') {
      if (dadosPessoa['cpfCnpj'].length == 11) {
        pessoaStorage['pessoaFisica']['cpf'] = dadosPessoa['cpfCnpj'];
        delete pessoaStorage['pessoaJuridica'];
      } else if (dadosPessoa['cpfCnpj'].length == 14) {
        delete pessoaStorage['pessoaFisica'];
        pessoaStorage['pessoaJuridica']['cnpj']          = dadosPessoa['cpfCnpj']
        pessoaStorage['pessoaJuridica']['nomeFantasia']  = dadosPessoa.nome;
      }
    } 
    //DADOS RECEBIDOS DO ENTPOINT /servercad/api/pessoa/{id}
    else {
      if (typeof dadosPessoa.pessoaFisica != 'undefined' && dadosPessoa.pessoaFisica != null)
      pessoaStorage['pessoaFisica']['cpf'] = dadosPessoa.pessoaFisica.cpf;
      else 
        delete pessoaStorage['pessoaFisica'];

      if (typeof dadosPessoa.pessoaJuridica != 'undefined' && dadosPessoa.pessoaJuridica != null) {
        pessoaStorage['pessoaJuridica']['cnpj']          = dadosPessoa.pessoaJuridica.cnpj;
        pessoaStorage['pessoaJuridica']['nomeFantasia']  = dadosPessoa.nome;
      } else {
        delete pessoaStorage['pessoaJuridica'];
      }
    }    

    this.setPessoa(pessoaStorage);
  }

}